import { Link, useLocation } from "react-router-dom";
import { Camera, Heart, Mail, Package, PenLine, QrCode, ShieldCheck, Video } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useEffect, useState } from "react";
import { API, api, money } from "@/lib/api";

const STEPS = [
  { icon: Video, title: "Upload your video", text: "A vertical phone video up to 3 minutes. Grandma's recipe, Dad's story, a birthday wish." },
  { icon: PenLine, title: "Add a title", text: "Give it a name and an optional short message for the people you love." },
  { icon: Package, title: "Choose your keepsakes", text: "Sticker sheets, ornaments or weatherproof QR stickers, as many as you like. FREE standard shipping in the lower 48 states." },
  { icon: QrCode, title: "Pick a design & we mail", text: "Holiday, Classic Scrapbook or Floral for your sticker sheet and ornament, each with your own QR code." },
];

const FAQ = [
  ["Who can watch my video?", "Your video is uploaded to our YouTube channel as UNLISTED. It does not show up in search, but anyone who has the link or scans a sticker can watch it and share it."],
  ["What kind of video works best?", "A video filmed upright (vertical) on your phone, under 3 minutes long. MP4 or MOV files from iPhone and Android work well."],
  ["Can I take the video down later?", "Yes. Open your keepsake under 'My keepsakes' and request removal. Please know that once removed, the printed stickers will no longer play the video."],
  ["What can I order?", "QR sticker sheets (15 matching QR stickers on one 5.83 x 8.27-inch sheet) and Holiday ornaments. Every item carries the same QR code that opens your video, and standard shipping is FREE to the lower 48 states and Washington, D.C. (We can't ship to Alaska, Hawaii or outside the U.S. yet.)"],
  ["Can I stop and finish later?", "Yes. Your progress is saved to your account. Sign in any time and choose 'Continue' on your keepsake."],
];

const Hero = () => (
  <section className="mx-auto max-w-6xl px-4 pt-6 sm:px-8 sm:pt-10" data-testid="landing-hero">
    <div className="overflow-hidden rounded-2xl border-4 border-white shadow-[0_12px_40px_rgba(44,30,22,0.15)]">
      <img src="/img/banner.jpg" width="1983" height="793" alt="Voices 2 Remember - Your voice. Your stories. Their keepsake." className="h-auto w-full" data-testid="landing-banner" />
    </div>
    <div className="fade-up mt-10 grid gap-8 md:grid-cols-12 md:items-end">
      <div className="md:col-span-8">
        <p className="font-script text-3xl text-[#A83232]">A voice they can keep forever</p>
        <h1 className="mt-2 text-4xl font-bold leading-tight text-[#4A2A18] sm:text-5xl lg:text-6xl">Turn a family video into stickers they can scan and hear.</h1>
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-[#4A3B32] sm:text-xl">Record a short story on your phone. We put your own QR code on stickers and holiday ornaments. One scan, and your video plays.</p>
      </div>
      <div className="flex flex-col md:col-span-4">
        <Link to="/examples" className="mx-auto flex w-3/4 items-center justify-center rounded-2xl bg-[#8B0A1A] px-5 py-4 text-3xl font-bold tracking-wide text-white shadow-lg transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#A3122A] sm:text-4xl" data-testid="hero-examples-button">Examples</Link>
        <span className="mx-auto mt-4 inline-flex -rotate-2 items-center gap-2 rounded-full border-2 border-dashed border-[#2E6930] bg-[#EEF5EA] px-4 py-1.5 text-base font-bold text-[#2E6930] shadow-sm" data-testid="hero-free-shipping-badge"><Package size={18} /> FREE shipping · lower 48 states</span>
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section id="how" className="scroll-mt-28 mx-auto mt-24 max-w-6xl px-4 sm:px-8" data-testid="how-it-works">
    <h2 className="text-3xl font-bold text-[#4A2A18] sm:text-4xl">How it works</h2>
    <p className="mt-2 text-lg text-[#5C4A3E]">Four simple steps. You can save and come back any time.</p>
    <ol className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {STEPS.map((s, i) => (
        <li key={s.title} className="paper-card relative p-6" data-testid={`how-step-${i + 1}`}>
          <span className="absolute -top-4 left-5 flex h-10 w-10 items-center justify-center rounded-full bg-[#A83232] font-serif text-xl font-bold text-white shadow">{i + 1}</span>
          <s.icon className="mt-3 text-[#C86446]" size={34} />
          <h3 className="mt-4 text-2xl font-semibold text-[#2C1E16]">{s.title}</h3>
          <p className="mt-2 text-lg leading-relaxed text-[#5C4A3E]">{s.text}</p>
        </li>
      ))}
    </ol>
    <Link to="/tips" className="mt-8 inline-flex items-center gap-2 text-lg font-semibold text-[#A83232] underline underline-offset-4" data-testid="how-tips-link">New to filming? Read our simple tips for a great memory video</Link>
  </section>
);

const Sample = () => (
  <section id="sample" className="scroll-mt-24 mt-24 bg-[#F2EBE1] py-20" data-testid="sample-section">
    <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-8 md:grid-cols-2 md:items-center">
      <div className="relative mx-auto w-full max-w-[300px]">
        <span className="tape -top-3 left-1/2 z-10 -translate-x-1/2 rotate-[-4deg]" />
        <div className="polaroid rotate-[-2deg]">
          <video src="/img/sample.mp4?v=2" poster="/img/sample-poster.jpg?v=2" controls playsInline className="aspect-[9/16] w-full bg-black object-cover" data-testid="sample-video" />
          <p className="font-script mt-3 text-center text-2xl text-[#4A3B32]">Grandma's bedtime story</p>
        </div>
      </div>
      <div>
        <p className="font-script text-3xl text-[#A83232]">Sample keepsake</p>
        <h2 className="mt-1 text-3xl font-bold text-[#4A2A18] sm:text-4xl">Scan the sticker, hear the story.</h2>
        <p className="mt-4 text-lg leading-relaxed text-[#4A3B32]">Every sticker carries the same QR code. It opens a private memory page with your video, ready to play on any phone camera. No app needed.</p>
        <div className="mt-8 flex flex-wrap items-center gap-6">
          <img src={`${API}/templates/classic_scrapbook/preview.png?title=Grandma's Bedtime Story&v=xmas`} alt="Sample QR sticker" className="h-48 w-48 rotate-[3deg] rounded-lg shadow-xl sm:h-56 sm:w-56" data-testid="sample-sticker-image" />
          <ul className="space-y-3 text-lg text-[#4A3B32]">
            <li className="flex gap-2"><Camera className="mt-1 shrink-0 text-[#C86446]" /> Point a phone camera at it</li>
            <li className="flex gap-2"><Video className="mt-1 shrink-0 text-[#C86446]" /> The video opens and plays</li>
            <li className="flex gap-2"><Mail className="mt-1 shrink-0 text-[#C86446]" /> Stick it on cards and gifts</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const Products = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/products").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  return (
    <section className="mx-auto mt-24 max-w-6xl px-4 sm:px-8" data-testid="products-section">
      <h2 className="text-3xl font-bold text-[#4A2A18] sm:text-4xl">Choose your keepsakes</h2>
      <p className="mt-2 text-lg text-[#5C4A3E]">Mix and match in one order. <b className="text-[#2E6930]">FREE standard shipping</b> to the lower 48 states and Washington, D.C.</p>
      <div className="mt-10 grid gap-8 sm:grid-cols-3">
        {items.map((p) => (
          <figure key={p.product_id} className="paper-card flex flex-col p-5 text-center" data-testid={`landing-product-${p.product_id}`}>
            <div className="flex h-56 items-center justify-center">
              <img src={p.kind === "ornament" ? "/img/ornament.png?v=1" : p.kind === "ornament_holidays" ? "/img/ornament-holidays.jpg?v=4" : p.kind === "sticker_sheet" ? "/img/stickers-sheet.jpg?v=2" : p.kind === "mug" ? "/img/mug.jpg?v=1" : p.kind === "qr_square" ? "/img/qr-square.jpg?v=2" : `${API}/products/${p.product_id}/preview.png?template=floral&v=xmas`} alt={p.name} className={`max-h-56 ${p.kind === "ornament" ? "w-auto drop-shadow-xl" : p.kind === "mug" ? "h-56 w-56 rounded-xl object-cover shadow-lg" : ["ornament_holidays", "qr_square"].includes(p.kind) ? "h-56 w-56 rounded-xl object-cover shadow-lg" : "w-auto rotate-[-2deg] rounded-sm shadow-lg"}`} />
            </div>
            <figcaption className="mt-5 font-serif text-2xl font-semibold text-[#2C1E16]">{p.name}</figcaption>
            <p className="text-base text-[#5C4A3E]">{p.variant}</p>
            <p className="mt-3 text-2xl font-bold text-[#A83232]" data-testid={`landing-price-${p.product_id}`}>{money(p.price)}<span className="ml-2 inline-block rounded-full bg-[#2E6930] px-3 py-0.5 align-middle text-sm font-bold uppercase tracking-wide text-white" data-testid={`free-shipping-badge-${p.product_id}`}>FREE Shipping</span></p>
          </figure>
        ))}
      </div>
    </section>
  );
};

const Designs = () => (
  <section className="mx-auto mt-24 max-w-6xl px-4 sm:px-8" data-testid="designs-section">
    <h2 className="text-3xl font-bold text-[#4A2A18] sm:text-4xl">Designs for your stickers</h2>
    <div className="mt-10 grid gap-8 sm:grid-cols-3">
      {[["holiday", "Holiday"], ["classic_scrapbook", "Classic Scrapbook"], ["floral", "Floral"]].map(([id, name], i) => (
        <figure key={id} className="paper-card p-5 text-center" data-testid={`design-card-${id}`}>
          <img src={`${API}/templates/${id}/preview.png?v=xmas`} alt={`${name} sticker`} className={`mx-auto w-48 rounded-md shadow-lg ${i % 2 ? "rotate-[2deg]" : "rotate-[-2deg]"}`} />
          <figcaption className="mt-5 font-serif text-2xl font-semibold text-[#2C1E16]">{name}</figcaption>
        </figure>
      ))}
    </div>
  </section>
);

const Trust = () => (
  <section className="mx-auto mt-24 grid max-w-6xl gap-10 px-4 sm:px-8 md:grid-cols-2" data-testid="faq-section">
    <div className="paper-card p-8">
      <ShieldCheck size={36} className="text-[#2E6930]" />
      <h2 className="mt-4 text-3xl font-bold text-[#4A2A18]">Simple and private</h2>
      <p className="mt-3 text-lg leading-relaxed text-[#4A3B32]">Each keepsake gets its own long, random link that search engines can't find. Only you, and the people you give stickers to, can open it.</p>
      <Link to="/create" className="btn-primary mt-8 w-full" data-testid="trust-create-button">Start my keepsake</Link>
    </div>
    <Accordion type="single" collapsible className="space-y-3">
      {FAQ.map(([q, a], i) => (
        <AccordionItem key={q} value={`q${i}`} className="paper-card px-5" data-testid={`faq-item-${i}`}>
          <AccordionTrigger className="text-left text-lg font-semibold text-[#2C1E16] hover:no-underline">{q}</AccordionTrigger>
          <AccordionContent className="text-lg leading-relaxed text-[#4A3B32]">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

const useHashScroll = () => {
  const { hash, key } = useLocation();
  useEffect(() => {
    if (!hash || hash.includes("session_id=")) return;
    const go = (behavior) => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior, block: "start" });
    const timers = [setTimeout(() => go("smooth"), 50), setTimeout(() => go("auto"), 900)];
    return () => timers.forEach(clearTimeout);
  }, [hash, key]);
};

export default function Landing() {
  useHashScroll();
  return (
    <div data-testid="landing-page">
      <Hero />
      <HowItWorks />
      <Sample />
      <Products />
      <Designs />
      <Trust />
    </div>
  );
}
